/**
 * Centralised TanStack Query keys for the Shifts resource, so the open/close
 * mutations and the current-shift query stay in step — the mutations invalidate
 * `current()` and the gate (`useShift().isOpen`) flips on the refetch. The
 * register and ledger-summary keys carry every input that changes their result.
 *
 * A shift belongs to the signed-in seller (BOS-166), so the current-shift key
 * carries the seller's id: the next seller to sign in on this terminal never
 * reads the previous seller's cached shift. `current()` with no id is the prefix
 * that invalidates every seller's entry. Expected cash is the seller's drawer,
 * so its key carries the seller too. A shift's status is read by whichever
 * seller is signed in on the device, so its key carries that seller as well as
 * the shift.
 */
export const ShiftQueryKeys = {
    all: ['shifts'] as const,
    current: (sellerId?: string | null) =>
        sellerId === undefined
            ? ([...ShiftQueryKeys.all, 'current'] as const)
            : ([...ShiftQueryKeys.all, 'current', sellerId] as const),
    status: (shiftId: string, sellerId: string | null) =>
        [...ShiftQueryKeys.all, 'status', shiftId, sellerId] as const,
    expected: (type: 'open' | 'close', sellerId: string | null) =>
        [...ShiftQueryKeys.all, 'expected', type, sellerId] as const,
    /** Every register page, whatever its filters: what a close invalidates. */
    registers: () => [...ShiftQueryKeys.all, 'register'] as const,
    register: (params: ShiftRegisterParams) =>
        [
            ...ShiftQueryKeys.all,
            'register',
            params.date,
            params.sellerId ?? '',
        ] as const,
    /** One shift's ledger summary in every locale. */
    ledgerSummaries: (shiftId: string) =>
        [...ShiftQueryKeys.all, 'ledgerSummary', shiftId] as const,
    ledgerSummary: (shiftId: string, locale: string) =>
        [...ShiftQueryKeys.all, 'ledgerSummary', shiftId, locale] as const,
};

/** The register's filters: one local open day and, optionally, one seller. */
export type ShiftRegisterParams = {
    date: string;
    sellerId: string | null;
};
